import { Course } from '../core/types';
import { createTariffCard } from './tariff-card';
import { createTariffComparison } from './tariff-comparison';
import { createElement, appendChildren } from '../utils/dom-helpers';
import { getCourseLinkById } from '../../../../pages/catalog/assets/js/utils/course-by-id';
import { getYmGoal } from '../../../../pages/catalog/assets/js/utils/helpYmGoals';

/**
 * Генерирует DOM-элемент десктопной модалки тарифов
 */
export function createTariffModal(course: Course): HTMLElement {
  // Фильтрация тарифов по is_active (0, false, пустой — пропускать)
  const filteredTariffs = (course.tariffs || []).filter(tariff => tariff.is_active !== 0 && tariff.is_active !== false && tariff.is_active !== '' && tariff.is_active !== undefined && tariff.is_active !== null);
  const modal = createElement('div', 'tarif__modal');

  // Header
  const header = createElement('div', 'modal__header');
  const headerInfo = createElement('div', 'header__info');
  const title = createElement('p', 'header__info__title');
  title.textContent = 'Тарифы';
  const desc = createElement('p', 'header__info__description');
  desc.textContent = course.short_title || course.title || 'Выберите подходящий тариф';

  // Получаем ссылку на курс через getCourseLinkById
  let aboutBtn: HTMLElement | null = null;
  const courseLink = getCourseLinkById(course.id);
  if (courseLink) {
    aboutBtn = createElement('a', 'header__info__about', {
      href: courseLink,
    });
    aboutBtn.textContent = 'О курсе';
    const ymGoal = getYmGoal('modal_about');
    if (ymGoal) aboutBtn.setAttribute('data-ym-goal', ymGoal);
    aboutBtn.setAttribute('data-ym-params', `{"course_id":"${course.id}"}`);
  }
  appendChildren(headerInfo, title, desc);
  if (aboutBtn) {
    appendChildren(headerInfo, title, desc, aboutBtn);
  }
  header.appendChild(headerInfo);

  // Проверяем, все ли тарифы имеют только полную цену
  const hasAllFullPriceOnly = filteredTariffs.every(tariff => 
    ((tariff.price_total || tariff.new_price_total) && !tariff.price_per_module && !tariff.new_price_per_module) ||
    (tariff.price_total && tariff.price_per_module && tariff.price_total === tariff.price_per_module) ||
    (tariff.new_price_total && tariff.new_price_per_module && tariff.new_price_total === tariff.new_price_per_module)
  );
  
  // Проверяем флаг is_show_as_course
  const isShowAsCourse = course.is_show_as_course === true || course.is_show_as_course === 1 || course.is_show_as_course === '1';
  
  // Переключатель цен
  const priceSwitcher = createElement('div', 'header__price__switcher');
  
  // Показываем только полную цену, если все тарифы имеют только полную цену ИЛИ установлен флаг is_show_as_course
  if (!hasAllFullPriceOnly && !isShowAsCourse) {
    // Если есть разные цены, создаём обычный переключатель с двумя опциями
    const monthly = createElement('div', 'switcher__item active', { 'data-price': 'monthly' });
    monthly.innerHTML = '<p>Помесячно</p>';
    const ymGoal = getYmGoal('monthly');
    if (ymGoal) monthly.setAttribute('data-ym-goal', ymGoal);
    
    const all = createElement('div', 'switcher__item', { 'data-price': 'all' });
    if (course.id === 1863 || course.id === 1864 || course.id === 1865 || course.id === 1866 || course.id === 1867 || course.id === 1868 || course.id === 1869 || course.id === 1870) {
      all.innerHTML = '<p>Год обучения</p>';
    } else {
      all.innerHTML = '<p>Сразу весь курс</p>';
    }
    const ymGoalFull = getYmGoal('full');
    if (ymGoalFull) all.setAttribute('data-ym-goal', ymGoalFull);
    all.setAttribute('data-ym-params', `{"course_id":"${course.id}"}`);
    
    if (course.total_price_old || course.total_price_discount) {
      // Use total_price_discount which is the actual property name from the data
      const discountValue = course.total_price_discount || course.total_percent_discount || 0;
      
      if (discountValue > 0) {
        const sticker = createElement('div', 'discount__sticker');
        sticker.innerHTML = `<span class="discount">-${discountValue}%</span>`;
        all.appendChild(sticker);
      }
    }
    
    appendChildren(priceSwitcher, monthly, all);
  } else {
    // Если все тарифы имеют только полную цену ИЛИ установлен флаг is_show_as_course,
    // создаём переключатель только с опцией "Сразу весь курс"
    const all = createElement('div', 'switcher__item active', { 'data-price': 'all' });
    if (course.id === 1863 || course.id === 1864 || course.id === 1865 || course.id === 1866 || course.id === 1867 || course.id === 1868 || course.id === 1869 || course.id === 1870) {
      all.innerHTML = '<p>Год обучения</p>';
    } else {
      all.innerHTML = '<p>Сразу весь курс</p>';
    }
    const ymGoalFull = getYmGoal('full');
    if (ymGoalFull) all.setAttribute('data-ym-goal', ymGoalFull);
    all.setAttribute('data-ym-params', `{"course_id":"${course.id}"}`);
    
    if (course.total_price_discount || course.total_percent_discount) {
      // Use total_price_discount which is the actual property name from the data
      const discountValue = course.total_price_discount || course.total_percent_discount || 0;
      
      if (discountValue > 0) {
        const sticker = createElement('div', 'discount__sticker');
        sticker.innerHTML = `<span class="discount">-${discountValue}%</span>`;
        all.appendChild(sticker);
      }
    }
    
    appendChildren(priceSwitcher, all);
    
    // Активируем отображение полной цены для всех тарифов
    setTimeout(() => {
      const tariffItems = document.querySelectorAll('.tariff__items .tarif');
      tariffItems.forEach(item => {
        const priceBlock = item.querySelector('.tarif__price');
        if (priceBlock) {
          const monthlyPrice = priceBlock.querySelector('.monthly-price');
          const fullPrice = priceBlock.querySelector('.full-price');
          if (monthlyPrice) monthlyPrice.classList.remove('active');
          if (fullPrice) {
            fullPrice.classList.add('active');
            (fullPrice as HTMLElement).style.display = 'flex';
          }
        }
      });
    }, 0);
  }
  
  header.appendChild(priceSwitcher);

  // Кнопка сравнения
  const compareBtn = createElement('button', 'btn-brdrd black', {
    id: 'tarif-compare',
    'data-action': 'toggle-tariff-comparison',
  });
  compareBtn.textContent = filteredTariffs.length === 1 ? 'Подробнее' : 'Сравнить тарифы';
  const ymGoalCompare = getYmGoal('compare');
  if (ymGoalCompare) compareBtn.setAttribute('data-ym-goal', ymGoalCompare);
  compareBtn.setAttribute('data-ym-params', `{"course_id":"${course.id}"}`);
  header.appendChild(compareBtn);

  // Кнопка закрытия
  const closeBtn = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  closeBtn.setAttribute('class', 'close__modal');
  closeBtn.setAttribute('width', '40');
  closeBtn.setAttribute('height', '40');
  closeBtn.setAttribute('viewBox', '0 0 40 40');
  closeBtn.setAttribute('fill', 'none');
  closeBtn.innerHTML = '<path d="M29.002 13.8281L22.8291 20.001L29.001 26.1729L26.1729 29.002L20.001 22.8291L13.8291 29.002L11.001 26.1729L17.1719 20.001L11 13.8281L13.8281 11L20.001 17.1729L26.1738 11L29.002 13.8281Z" fill="#808080" />';
  const ymGoalClose = getYmGoal('close_tariff');
  if (ymGoalClose) closeBtn.setAttribute('data-ym-goal', ymGoalClose);
  closeBtn.setAttribute('data-ym-params', `{"course_id":"${course.id}"}`);
  header.appendChild(closeBtn);

  modal.appendChild(header);

  // Body
  const body = createElement('div', 'modal__body');
  
  // Tariff items с активным классом (как в PHP)
  const items = createElement('div', 'tariff__items active');
  if (filteredTariffs.length) {
    filteredTariffs.forEach((tariff, index) => {
      const card = createTariffCard(tariff, course, index);
      items.appendChild(card);
    });
  } else {
    const empty = createElement('div', 'tarif');
    empty.textContent = 'Тарифы для данного курса недоступны.';
    items.appendChild(empty);
  }
  body.appendChild(items);
  
  // Добавляем сравнение тарифов
  body.appendChild(createTariffComparison({...course, tariffs: filteredTariffs}));
  modal.appendChild(body);

  // Footer
  const footer = createElement('div', 'modal__footer');
  const help = createElement('p', '');
  help.textContent = 'Нужна помощь с выбором?';
  const callback = createElement('p', '');
  callback.setAttribute('data-action', 'callback-modal');
  callback.textContent = 'Оставить заявку';
  appendChildren(footer, help, callback);
  modal.appendChild(footer);

  return modal;
} 